import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { ClipboardList, Calendar, AlertTriangle, CheckCircle, TrendingUp, Plus, Search } from 'lucide-react';

function ConsumablePlanning() {
  const { API } = useContext(AppContext);
  const [planningData, setPlanningData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('February 2025');

  useEffect(() => {
    fetchPlanningData();
  }, [selectedMonth]);

  const fetchPlanningData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/planning`, {
        params: { month: selectedMonth },
        
      });
      setPlanningData(response.data);
    } catch (error) {
      console.error('Error fetching planning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Adequate': return 'bg-green-100 text-green-700';
      case 'Low': return 'bg-orange-100 text-orange-700';
      case 'Critical': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Production': return 'bg-blue-100 text-blue-700';
      case 'Dispatch': return 'bg-purple-100 text-purple-700';
      case 'General': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = {
    totalItems: planningData.length,
    adequate: planningData.filter(p => p.status === 'Adequate').length,
    lowStock: planningData.filter(p => p.status === 'Low').length,
    critical: planningData.filter(p => p.status === 'Critical').length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading planning data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="consumable-planning-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consumable Planning</h1>
          <p className="text-gray-500 mt-1">Monthly consumable requirement planning and forecasting</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none bg-white"
          >
            <option>January 2025</option>
            <option>February 2025</option>
            <option>March 2025</option>
          </select>
          <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
            <Plus size={18} />
            <span>Add Plan</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2">
            <ClipboardList className="text-[#1a237e]" size={18} />
            <p className="text-sm text-gray-500">Total Items</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.totalItems}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Adequate Stock</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.adequate}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2">
            <TrendingUp className="text-orange-500" size={18} />
            <p className="text-sm text-orange-600 font-medium">Low Stock</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{stats.lowStock}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="text-red-500" size={18} />
            <p className="text-sm text-red-600 font-medium">Critical</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.critical}</p>
        </div>
      </div>

      {/* Planning Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
            <Calendar size={16} className="text-[#1a237e]" />
            <span>{selectedMonth} Planning</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Current Stock</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Monthly Usage</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Planned Qty</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Reorder Level</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {planningData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.item}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">{item.currentStock.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.monthlyUsage.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4 text-sm font-medium text-[#1a237e]">{item.plannedQty.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4 text-sm text-gray-500">{item.reorderLevel.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ConsumablePlanning;
